import { actions } from "./slice";
import { db } from "initializer";
import sendEvent from "modules/sendEvent";
import { createSubscribeCollection } from "../firestoreModuleUtils/operators";
import { sendDiceBotCommand, deleteAllRoomMessages, rollTable } from "api";
import { getChannelName, getFirstRoomMessage } from "./selectors";
import {
  getCharacterById,
  getUserCharacterId,
} from "../entities.room.characters/selectors";
import { getRoomById } from "../entities.rooms/selectors";
import { getAppState } from "../app.state/selectors";
import { getAppChatState } from "../app.chat/selectors";
import { Character, UpdateCharacter } from "../entities.room.characters/";
import {
  addUndoUpdateCharacter,
  updateRoomCharacter,
} from "../entities.room.characters/operations";
import { activeRoomEffectByText } from "../entities.room.effects/operations";
import {
  addUndoSceneChange,
  applyScene,
  preloadSceneResources,
} from "../entities.room.scenes/operations";
import { appStateMutate } from "../app.state/operations";
import { appChatStateMutate } from "../app.chat/operations";
import { Message, MessageType, UpdateMessage } from "./";
import { DefaultThunk } from "stores";
import {
  updateCurrentRoom,
  updateCurrentRoomField,
} from "../entities.rooms/operations";
import shortid from "shortid";
import ReactPlayer from "react-player";
import { ThunkDispatch } from "redux-thunk";
import { DefaultRootState } from "stores";
import { getRoomSceneIdByName } from "../entities.room.scenes/selectors";
import { getCurrentRoom } from "../entities.rooms/selectors";
import {
  getRoomSavedataByName,
  getRoomSavedataIdByName,
} from "../entities.room.savedatas/selectors";
import { produce } from "immer";
import {
  addDoc,
  collection,
  limit,
  orderBy,
  query,
  Timestamp,
  serverTimestamp,
  setDoc,
  doc,
  deleteDoc,
  getDocs,
  where,
  startAfter,
  writeBatch,
  getDoc,
  getCountFromServer,
  QueryDocumentSnapshot,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";
import { filterDiceSkinSettings } from "modules/diceSkin";
import { getHasEditableRole } from "../entities.room.members/selectors";
import YouTubePlayer from "react-player/youtube";
import { loadRoomBySnapshotId } from "../entities.room.savedatas/operations";
import { AES } from "crypto-js";
import Utf8 from "crypto-js/enc-utf8";
import i18next from "i18next";
import toCDNUrl from "modules/toCDNUrl";
import { AudioPlayer } from "modules/audioplayer";
import { getIsPro } from "../app.user/selectors";
import { saveAs } from "file-saver";
import { FileEntries, FileEntry, zip } from "modules/exporter";
import { CachedDataUrl } from "modules/cachedDataUrl";
import { escape, forEach, reverse, template, uniq } from "lodash-es";
import { renderRoomLogHTML } from "modules/roomLogHtml";
import type { RoomLogHtmlMessage } from "modules/roomLogHtml";
import { RoomLogHtmlImageCache } from "modules/roomLogHtmlImage";

export const DEFAULT_COMMAND = ["initiative"];

const messagesRef = (roomId: string) =>
  collection(db, "rooms", roomId, "messages");

export const shiftAddedRoomMessage = (): DefaultThunk => (dispatch) => {
  return dispatch(actions.shiftAddedRoomMessage());
};

export const subscribeRoomMessages = createSubscribeCollection(
  // TODO: fix any
  actions as any,
  (roomId: string) =>
    query(messagesRef(roomId), orderBy("createdAt", "desc"), limit(50)),
  true
);

const getMessageType = (text: string) => {
  if (!text) return "text";
  const matcher = /^\/(.+?)\s/;
  const matches = text.match(matcher);
  if (matches) {
    if (matches[1] === "note") {
      return "note";
    } else if (matches[1] === "system") {
      return "system";
    }
  }
  return "text";
};
const trimText = (text: string) => {
  if (!text) return "";
  const prefixMatcher = /^\/(.+?)\s/;
  const suffixMatcher = /@\S+$/;
  return text.replace(prefixMatcher, "").replace(suffixMatcher, "");
};
const MAX_EXPAND_TIMES = 5;
const MAX_EXPAND_STRING_SIZE = 10000;

const expandParams = (
  text: string,
  find: (label: string) => string | undefined
): string => {
  const regexp = /\{([^}]+)\}/gm;
  const replacer = (match: string, label: string) => {
    const s = find(label);
    return s == null ? match : s;
  };

  let prevText = "";
  for (
    let i = 0;
    i < MAX_EXPAND_TIMES &&
    prevText !== text &&
    text.length < MAX_EXPAND_STRING_SIZE;
    i++
  ) {
    prevText = text;
    text = text.replace(regexp, replacer);
  }

  return text;
};

const getUserDefine = (character: Character) => {
  return character.commands
    .split(/[\r\n]+/)
    .map((s) => String(s))
    .filter((str) => str.slice(0, 2) === "//")
    .map((userdef) =>
      userdef.slice(2).replace(/[　]/g, " ").replace(/[＝]/g, "=")
    );
};

const findRoomParam = (
  room: { variables: { label: string; value: string }[] },
  label: string
) => {
  const upperCaseLabel = label.toUpperCase();
  const variable = room.variables.find(
    (variable) => variable.label.toUpperCase() === upperCaseLabel
  );

  return variable?.value;
};

const findCharacterParam = (character: Character, label: string) => {
  const userdefs = getUserDefine(character);
  const upperCaseLabel = label.toUpperCase();
  const isMaxSuffix = upperCaseLabel.endsWith(".MAX");
  const baseLabel = isMaxSuffix ? upperCaseLabel.slice(0, -4) : upperCaseLabel;
  const state = character.status.find(
    (state) => state.label && state.label.toUpperCase() === baseLabel
  );
  if (isMaxSuffix) {
    return state ? state.max.toString() : undefined;
  }
  const param = character.params.find(
    (param) => param.label && param.label.toUpperCase() === baseLabel
  );
  const userdef = userdefs.find((def) => {
    const variable = def.split("=", 1)[0].trim().toUpperCase();
    return variable === baseLabel;
  });

  if (upperCaseLabel === "INITIATIVE" || upperCaseLabel === "イニシアティブ") {
    return character.initiative.toString();
  } else if (state) {
    return state.value.toString();
  } else if (param) {
    return param.value;
  } else if (userdef) {
    let tmp = userdef.split("=");
    tmp.shift();
    return tmp.join("=").trim();
  }

  return;
};

const expandRoomParams = (
  text: string,
  room: { variables: { label: string; value: string }[] }
) => {
  return expandParams(text, (p1) => findRoomParam(room, p1));
};

const expandCharacterParams = (text: string, character: Character) => {
  return expandParams(text, (p1) => findCharacterParam(character, p1));
};

const expandMessageParams = (
  text: string,
  room: { variables: { label: string; value: string }[] },
  character: Character
) => {
  let expandedText = expandRoomParams(text, room);
  if (character._id) {
    expandedText = expandCharacterParams(expandedText, character);
  }
  return expandedText;
};

const expandCommandParams = (
  text: string,
  room: { variables: { label: string; value: string }[] },
  character: Character
) => {
  return expandMessageParams(text, room, character);
};

type Control = {
  label: string;
  target: "value" | "max";
  ope: "+" | "-" | "=";
  val: number | "max";
  rest: string;
};

const parseControl = (text: string): Control | null => {
  const reg = /^:(.+)(\+|-|=)(\d+|max)(.*)/i;
  const res = reg.exec(text);
  if (res === null) return null;
  let label = res[1];
  const ope = res[2] as "+" | "-" | "=";
  const valStr = res[3];
  const rest = res[4];
  const val: number | "max" =
    valStr.toLowerCase() === "max" ? "max" : parseInt(valStr);
  if (typeof val === "number" && isNaN(val)) return null;
  // val === "max" is only valid with ope === "="
  if (val === "max" && ope !== "=") return null;
  // .max suffix → target the max field of the status
  const isMaxTarget = label.toLowerCase().endsWith(".max");
  const target: "value" | "max" = isMaxTarget ? "max" : "value";
  if (isMaxTarget) {
    label = label.slice(0, -4);
    if (label.length === 0) return null;
  }
  // setting max to max is a no-op
  if (target === "max" && val === "max") return null;
  switch (ope) {
    case "+":
    case "-":
    case "=":
      return { label, target, ope, val, rest };
  }
  return null;
};

const changeValueWithMessage =
  (
    control: Control,
    type: "status" | "default",
    idx: number,
    character: Character,
    roomId: string
  ): DefaultThunk =>
  (dispatch) => {
    if (character._id === undefined) return;
    // initiative などの default-type フィールドは max を持たない
    if (type === "default" && control.target === "max") return;
    const statusItem = type === "status" ? character[type][idx] : null;
    const currentMax = statusItem?.max ?? 0;
    const resolvedVal = control.val === "max" ? currentMax : control.val;
    const currentValue =
      control.target === "max"
        ? currentMax
        : type === "status"
        ? character[type][idx].value
        : character[control.label];
    const nextValue =
      control.ope === "+"
        ? currentValue + resolvedVal
        : control.ope === "-"
        ? currentValue - resolvedVal
        : resolvedVal;
    let updateData: UpdateCharacter = {};
    if (type === "status") {
      const field = control.target;
      const nextValues = character[type].map((v) =>
        v.label === control.label ? { ...v, [field]: nextValue } : v
      );
      updateData = { [type]: nextValues };
      dispatch(updateRoomCharacter(character._id, updateData));
    }
    if (type === "default") {
      updateData = { [control.label]: nextValue };
      dispatch(updateRoomCharacter(character._id, updateData));
    }
    const fieldLabel =
      control.target === "max" ? `${control.label}最大値` : control.label;
    if (!character.secret) {
      dispatch(
        addMessage(
          roomId,
          null,
          {
            text: `/system [ ${character.name} ] ${fieldLabel} : ${currentValue} → ${nextValue}`,
            name: "system",
            channel: "main",
          },
          false
        )
      );
      dispatch(
        addUndoUpdateCharacter(character._id, updateData, {
          label: control.label,
          type: type,
          target: control.target,
        })
      );
    } else {
      dispatch(addUndoUpdateCharacter(character._id, updateData));
    }
    return;
  };

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

type TextData = {
  to?: string;
  roomChatToName?: string;
  name?: string;
  iconUrl?: string | null;
  text?: string;
  color?: string;
  channel?: string;
  channelName?: string;
  type?: "note" | "scene";
};

const SYSTEM_MESSAGE_COLOR = "#888888";

export const addMessage =
  (
    roomId: string,
    characterId: string | null,
    textData: TextData = {},
    clearText: boolean = true,
    standalone: boolean = false
  ): DefaultThunk =>
  async (dispatch, getState) => {
    const startTime = Date.now();
    const state = getState();
    const room = getRoomById(state, roomId);
    const overrideCharacterId = getAppChatState(state, "overrideCharacterId");
    const selectCharacterId = !!textData.type // typeが設定されている場合は一時キャラクターを利用しない
      ? characterId
      : overrideCharacterId || characterId;
    const character = getCharacterById(state, selectCharacterId);
    const from = getAppState(state, "uid");
    const to = textData.to || getAppState(state, "roomChatTo");
    const toName =
      textData.roomChatToName || getAppState(state, "roomChatToName");
    const name =
      textData.name || character.name || getAppState(state, "roomChatName");
    const iconUrl = textData.iconUrl || null;
    const imageUrl = textData.iconUrl || null;
    const inputText = textData.text || getAppChatState(state, "inputText");
    const color =
      textData.color ||
      (selectCharacterId ? character.color : null) || // character.color がない場合は roomChatColor を使う
      getAppState(state, "roomChatColor");
    const channel = textData.channel || getAppState(state, "roomChatTab");
    const channelName = textData.channelName || getChannelName(state, channel);
    const diceSkinSettings = character._id
      ? character.diceSkin
      : state.entities.userSetting.diceSkin;
    const isPro = getAppState(state, "plan") === "ccfolia-pro";

    if (!room || !from || !inputText) return;

    const slashCommand = getSlashCommand(inputText);
    if (slashCommand != null) {
      const expandedInputText =
        slashCommand.name === "var"
          ? expandCommandParams(inputText, room, character)
          : inputText;
      const handled = await slashCommand.handler({
        roomId,
        characterId: selectCharacterId,
        textData,
        inputText: expandedInputText,
        startTime,
        isPro,
        dispatch,
        getState,
        isStandalone: standalone,
      });

      if (handled) {
        if (clearText) {
          dispatch(
            appChatStateMutate((state) => {
              state.inputText = "";
              state.overrideCharacterId = null;
            })
          );
        }
        return;
      }
    }

    // secret function
    if (inputText === "/preload") {
      return dispatch(preloadSceneResources());
    }
    if (inputText === "/open scenes") {
      window.open(
        `/rooms/${roomId}/studio`,
        "ccfolia.room.scenes",
        "top=100,left=100,width=320,height=480"
      );
      return;
    }

    const data = {
      to,
      toName,
      from,
      name,
      iconUrl,
      color,
      imageUrl,
      text: inputText,
    };

    // clear text field
    if (clearText) {
      dispatch(
        appChatStateMutate((state) => {
          state.inputText = "";
          state.overrideCharacterId = null;
        })
      );
    }

    const type = getMessageType(data.text);
    if (type === "note" || type === "system") {
      // シナリオテキストとシステムメッセージの場合、色をデフォルトに変更
      data.color = SYSTEM_MESSAGE_COLOR;
    }

    if (type === "text") {
      data.text = data.text.startsWith(":")
        ? expandCommandParams(data.text, room, character)
        : expandMessageParams(data.text, room, character);
    }

    // variables
    if (character._id && type === "text") {
      const control = parseControl(data.text);
      if (control !== null) {
        const stateIdx = character.status.findIndex(
          (state) => state.label === control.label
        );
        const defaultCommandIdx = DEFAULT_COMMAND.findIndex(
          (command) => command === control.label
        );
        if (stateIdx !== -1) {
          dispatch(
            changeValueWithMessage(
              control,
              "status",
              stateIdx,
              character,
              roomId
            )
          );
          return;
        } else if (defaultCommandIdx !== -1) {
          dispatch(
            changeValueWithMessage(
              control,
              "default",
              defaultCommandIdx,
              character,
              roomId
            )
          );
          return;
        }
      }
      data.name = character.name;
      data.iconUrl = character.iconUrl;
      data.imageUrl = character.invisible ? null : character.iconUrl;
    }

    // roll
    const roll = await sendDiceBotCommand(room.diceBotSystem, data.text);
    const extend = roll
      ? {
          roll: {
            ...roll,
            skin: filterDiceSkinSettings(
              diceSkinSettings,
              room.diceBotSystem,
              isPro
            ),
          },
        }
      : {};

    // faces
    if (character.faces) {
      const face = character.faces
        .slice()
        .sort((a, b) => {
          return b.label.length - a.label.length;
        })
        .find((face) => {
          if (!face.label) return false;
          const endsWithText = data.text.endsWith(face.label);
          const endsWithDiceResult = roll && roll.result.endsWith(face.label);
          return endsWithText || endsWithDiceResult;
        });
      if (face) {
        data.iconUrl = face.iconUrl;
        data.imageUrl = character.invisible ? null : face.iconUrl;
        if (character._id !== undefined) {
          dispatch(
            updateRoomCharacter(character._id, { iconUrl: face.iconUrl })
          );
          dispatch(
            addUndoUpdateCharacter(character._id, { iconUrl: face.iconUrl })
          );
        }
      }
    }

    // effects
    const effectText = extend.roll ? extend.roll.result : data.text;
    if (
      (channel === "main" || channel === "info" || channel === "other") &&
      (!roll || !roll.secret)
    ) {
      dispatch(activeRoomEffectByText(roomId, effectText));
    }

    const displayText = trimText(data.text);
    if (displayText) {
      return addDoc(messagesRef(roomId), {
        ...data,
        text: displayText,
        type,
        channel,
        channelName,
        extend,
        edited: false,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }).then((res) => {
        sendEvent("addRoomMessage", { value: ~~(Date.now() - startTime) });
        return res;
      });
    }
  };

export const addMessageCurrentRoom =
  (text: string, clearText: boolean = true): DefaultThunk =>
  (dispatch, getState) => {
    const state = getState();
    const roomId = getAppState(state, "roomId");
    const characterId = getUserCharacterId(state);

    if (roomId == null) {
      return;
    }

    dispatch(addMessage(roomId, characterId || null, { text }, clearText));
  };

type AddMessageWithRollOptions = {
  roll: NonNullable<Message["extend"]["roll"]>;
  textData: TextData;
};

export const addMessageWithRoll =
  ({ textData, roll }: AddMessageWithRollOptions): DefaultThunk =>
  async (dispatch, getState) => {
    const startTime = Date.now();
    const state = getState();

    const roomId = getAppState(state, "roomId") || "";
    const room = getRoomById(state, roomId);

    const from = getAppState(state, "uid");
    const to = textData.to || getAppState(state, "roomChatTo");
    const toName =
      textData.roomChatToName || getAppState(state, "roomChatToName");
    const name = textData.name || getAppState(state, "roomChatName");
    const characterId = getUserCharacterId(state);
    const character = getCharacterById(state, characterId || "");
    const iconUrl = textData.iconUrl || null;
    const imageUrl = textData.iconUrl || null;
    const inputText = textData.text || getAppChatState(state, "inputText");
    const color =
      textData.color ||
      (characterId ? character.color : null) || // character.color がない場合は roomChatColor を使う
      getAppState(state, "roomChatColor");
    const channel = textData.channel || getAppState(state, "roomChatTab");
    const channelName = textData.channelName || getChannelName(state, channel);
    const diceSkinSettings = character._id
      ? character.diceSkin
      : state.entities.userSetting.diceSkin;
    const isPro = getAppState(state, "plan") === "ccfolia-pro";

    if (!room || !from || !inputText) return;

    const data = {
      to,
      toName,
      from,
      name,
      iconUrl,
      color,
      imageUrl,
      text: inputText,
    };

    const type = getMessageType(data.text);

    // variables
    if (character._id && type === "text") {
      data.name = character.name;
      data.iconUrl = character.iconUrl;
      data.imageUrl = character.invisible ? null : character.iconUrl;
    }

    // roll
    const extend: Message["extend"] = {
      roll: {
        ...roll,
        skin: filterDiceSkinSettings(
          diceSkinSettings,
          room.diceBotSystem,
          isPro
        ),
      },
    };

    // faces
    if (character.faces) {
      const face = character.faces
        .slice()
        .sort((a, b) => {
          return b.label.length - a.label.length;
        })
        .find((face) => {
          if (!face.label) return false;
          const endsWithText = data.text.endsWith(face.label);
          const endsWithDiceResult = roll && roll.result.endsWith(face.label);
          return endsWithText || endsWithDiceResult;
        });
      if (face) {
        data.iconUrl = face.iconUrl;
        data.imageUrl = character.invisible ? null : face.iconUrl;
        if (character._id !== undefined) {
          dispatch(
            updateRoomCharacter(character._id, { iconUrl: face.iconUrl })
          );
          dispatch(
            addUndoUpdateCharacter(character._id, { iconUrl: face.iconUrl })
          );
        }
      }
    }

    // effects
    const effectText = extend.roll ? extend.roll.result : data.text;
    if (
      (channel === "main" || channel === "info" || channel === "other") &&
      (!roll || !roll.secret)
    ) {
      dispatch(activeRoomEffectByText(roomId, effectText));
    }

    const displayText = trimText(data.text);
    if (displayText) {
      return addDoc(messagesRef(roomId), {
        ...data,
        text: displayText,
        type,
        channel,
        channelName,
        extend,
        edited: false,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }).then((res) => {
        sendEvent("addRoomMessage", { value: ~~(Date.now() - startTime) });
        return res;
      });
    }
  };

export const updateRoomMessage =
  (
    messageId: string,
    data: {
      text?: string;
      extend?: {
        roll: {
          secret: boolean;
        };
      };
    },
    edited: boolean = true
  ): DefaultThunk =>
  (_, getState) => {
    const roomId = getState().app.state.roomId;
    if (!roomId) return null;
    return setDoc(
      doc(messagesRef(roomId), messageId),
      {
        ...data,
        edited,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

export const deleteMessage = (roomId: string, messageId: string) => () => {
  return deleteDoc(doc(messagesRef(roomId), messageId));
};

export const deleteAllMessages = (roomId: string) => async (dispatch) => {
  dispatch(
    appStateMutate((state) => {
      state.loading = true;
    })
  );
  try {
    await deleteAllRoomMessages(roomId, {
      eventTrigger: "click-button-on-chat",
    });
  } catch (_) {
    window.alert(i18next.t("操作が完了できませんでした。"));
  }
  dispatch(
    appStateMutate((state) => {
      state.loading = false;
    })
  );
};

export const loadRoomMessages =
  (): DefaultThunk<Promise<number | null>> =>
  async (dispatch, getState): Promise<number | null> => {
    const state = getState();
    const roomId = state.app.state.roomId;
    const channel = state.app.state.roomChatTab;
    const isLoading = state.app.state.roomChatChannelLoading[channel];
    const isLoaded = state.app.state.roomChatChannelLoaded[channel] ?? false;
    if (!roomId || isLoading || isLoaded || !channel) return null;

    const lastMessage = getFirstRoomMessage(state);
    const lastTime = lastMessage?.createdAt ?? Timestamp.now();

    dispatch(
      appStateMutate((state) => {
        state.roomChatChannelLoading[channel] = true;
      })
    );

    return getDocs(
      query(
        messagesRef(roomId),
        where("channel", "==", channel),
        orderBy("createdAt", "desc"),
        startAfter(lastTime),
        limit(20)
      )
    )
      .then((snapshot) => {
        const addActions = snapshot.docs.map((doc) => {
          return actions.add({
            id: doc.id,
            entity: {
              ...(doc.data() as Message),
              _id: doc.id,
              removed: true,
            },
            reversed: true,
          });
        });

        for (const action of addActions) {
          dispatch(action);
        }

        if (snapshot.size < 20) {
          dispatch(
            appStateMutate((state) => {
              state.roomChatChannelLoaded[channel] = true;
            })
          );
        }

        return snapshot.size;
      })
      .finally(() => {
        dispatch(
          appStateMutate((state) => {
            state.roomChatChannelLoading[channel] = false;
          })
        );
      });
  };

export const DEFAULT_TEXT_COLOR_CODE = "#888888";

export const addDummyMessages = (): DefaultThunk => async (_, getState) => {
  const state = getState();
  const roomId = getAppState(state, "roomId");
  const from = getAppState(state, "uid");

  if (!roomId || !from) {
    return;
  }

  const batchId = shortid();
  const now = Date.now();
  const batch = writeBatch(db);
  for (let i = 0; i < 100; i++) {
    const id = shortid();
    const text = i.toString().repeat(Math.random() * 100 + 1) + ":" + batchId;
    const createdAt = Timestamp.fromMillis(now + i);
    batch.set(doc(messagesRef(roomId), id), {
      to: null,
      toName: "",
      from,
      name: "Dummy User",
      iconUrl: null,
      color: DEFAULT_TEXT_COLOR_CODE,
      imageUrl: null,
      text,
      type: "text",
      channel: "main",
      channelName: "main",
      extend: {},
      edited: false,
      updatedAt: createdAt,
      createdAt,
    });
  }

  batch.commit();
};

type SlashCommandHandler = (
  props: SlashCommandHandlerProps
) => Promise<boolean>;
type SlashCommandHandlerProps = {
  roomId: string;
  characterId: string | null;
  textData: TextData;
  inputText: string;
  startTime: number;
  isPro: boolean;
  isStandalone: boolean;
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>;
  getState: () => DefaultRootState;
};

type SlashCommand = {
  name: string;
  handler: SlashCommandHandler;
};

const getSlashCommand = (inputText: string): SlashCommand | null => {
  const [prefix] = inputText.split(/\s/, 2);
  // [a-zA-Z0-9_] とハイフンのみを許可
  const matched = /^\/([\w-]+)$/.exec(prefix);
  if (matched == null) {
    return null;
  }

  const commandName = matched[1];
  if (isRepeated(commandName)) {
    return { name: commandName, handler: nopHandler };
  }

  const handler = slashCommands[commandName];
  if (!handler) {
    return null;
  }

  // handlerが存在する場合はrepeatを記録
  markRepeated(commandName);
  return { name: commandName, handler };
};

const WAIT_MS = 250;
const repeated: Record<string, boolean> = {};

const isRepeated = (commandName: string) => {
  return repeated[commandName];
};

const markRepeated = (commandName: string) => {
  repeated[commandName] = true;
  window.setTimeout(() => {
    repeated[commandName] = false;
  }, WAIT_MS);
};

const omikujiPool: string[] = [
  "大吉",
  "吉",
  "中吉",
  "小吉",
  "末吉",
  "凶",
  "大凶",
];

const getRandomInt = (n: number): number => {
  return Math.floor(Math.random() * n);
};

const omikujiHandler: SlashCommandHandler = async ({
  roomId,
  characterId,
  textData,
  dispatch,
  getState,
  startTime,
  isPro,
}) => {
  if (!isPro) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/omikuji コマンドは CCFOLIA PRO のみ利用可能な機能です。"
          ))
      )
    );
    return true;
  }

  const chosenIndex = getRandomInt(omikujiPool.length);

  const message: UpdateMessage = {
    text: i18next.t("今日のあなたの運勢は……？"),
    extend: {
      roll: {
        result: `\n【${omikujiPool[chosenIndex]}】`,
        dices: [],
        secret: false,
        success: false,
        failure: false,
        critical: false,
        fumble: false,
      },
    },
  };

  await sendMessage({
    message,
    type: "text",
    roomId,
    characterId,
    textData,
    getState,
    startTime,
    dispatch,
  });

  return true;
};

const playHandler: SlashCommandHandler = async ({
  inputText,
  getState,
  isPro,
  dispatch,
}) => {
  const url = inputText.split(" ")[1];

  if (!url) {
    return false;
  }

  if (!isValidUrl(url) || !ReactPlayer.canPlay(url)) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t("再生できないURLです。"))
      )
    );
    return true;
  }

  if (!(isPro || YouTubePlayer.canPlay(url))) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/play コマンドでの動画ファイルの再生は CCFOLIA PRO のみ利用可能な機能です。"
          ))
      )
    );
    return true;
  }

  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/play コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  dispatch(updateCurrentRoom({ video: { id: shortid.generate(), url } }));
  return true;
};

const pdfHandler: SlashCommandHandler = async ({
  inputText,
  dispatch,
  isStandalone,
}) => {
  const url = inputText.split(" ")[1];

  if (!url) {
    return false;
  }

  if (isStandalone) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/pdf コマンドは別窓表示では利用できません。"
          ))
      )
    );
    return true;
  }

  if (!isValidUrl(url)) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t("表示できないURLです。"))
      )
    );
    return true;
  }

  dispatch(
    appStateMutate((state) => {
      const prevOpen = state.openRoomDisplays;
      state.openPDFViewer = true;
      state.openPDFViewerUrl = url;
      if (prevOpen !== "pdf") {
        state.prevOpenRoomDisplay = prevOpen;
      }
      state.openRoomDisplays = "pdf";
    })
  );
  return true;
};

const loadHandler: SlashCommandHandler = async ({
  inputText,
  getState,
  dispatch,
}) => {
  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/load コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const [line] = inputText.split("\n", 1);
  const savedataName = line.slice(6).trim(); // trim "/save "

  const state = getState();
  const savedataId = getRoomSavedataIdByName(state, savedataName);
  if (savedataId == null) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "指定されたセーブデータがありませんでした。\n[セーブデータ名] {{savedataName}}",
            { savedataName }
          ))
      )
    );
    return true;
  }

  dispatch(
    appStateMutate((state) => {
      state.openLoadConfirmId = savedataId;
      state.openLoadConfirm = true;
    })
  );
  return true;
};

const saveHandler: SlashCommandHandler = async ({
  inputText,
  getState,
  dispatch,
}) => {
  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/save コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const [line] = inputText.split("\n", 1);
  const savedataName = line.slice(6).trim(); // trim "/save "

  const state = getState();
  const savedata = getRoomSavedataByName(state, savedataName);
  if (savedata == null) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "指定されたセーブデータがありませんでした。\n[セーブデータ名] {{savedataName}}",
            { savedataName }
          ))
      )
    );
    return true;
  }

  dispatch(
    appStateMutate((state) => {
      state.openSaveConfirm = savedata.order;
      state.saveConformDisabledNameEdit = true;
    })
  );
  return true;
};

const resetHandler: SlashCommandHandler = async ({ getState, dispatch }) => {
  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/reset コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const state = getState();
  const room = getCurrentRoom(state);
  if (room?.initialSavedata == null) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "このルームでは /reset コマンドを実行できません。"
          ))
      )
    );
    return true;
  }

  if (!window.confirm(i18next.t("ルームをルーム作成時の状態に戻しますか？"))) {
    return true;
  }

  dispatch(loadRoomBySnapshotId(room.initialSavedata.snapshotId));
  return true;
};

type SendMessageProps = {
  message: UpdateMessage;
  type: MessageType;
  roomId: string;
  characterId: string | null;
  textData: TextData;
  startTime: number;
  getState: () => DefaultRootState;
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>;
};

const sendMessage = ({
  message,
  type,
  roomId,
  characterId,
  textData,
  getState,
  startTime,
  dispatch,
}: SendMessageProps): Promise<any> => {
  const state = getState();
  const character = getCharacterById(state, characterId);
  const from = getAppState(state, "uid") || undefined;
  const to = textData.to || getAppState(state, "roomChatTo");
  const toName =
    textData.roomChatToName || getAppState(state, "roomChatToName");
  const name =
    textData.name || character.name || getAppState(state, "roomChatName");
  const iconUrl = textData.iconUrl || null;
  const imageUrl =
    character.invisible || !textData.iconUrl ? null : textData.iconUrl;
  const color =
    textData.color || character.color || getAppState(state, "roomChatColor");
  const channel = textData.channel || getAppState(state, "roomChatTab");
  const channelName = textData.channelName || getChannelName(state, channel);

  const data: UpdateMessage = {
    to,
    toName,
    from,
    name,
    iconUrl,
    color,
    imageUrl,
    type,
    channel,
    channelName,
    extend: {},

    ...message,

    edited: false,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  };

  // effects
  const roll = data.extend?.roll;
  const effectText = roll?.result ?? data.text;
  if (
    effectText &&
    (channel === "main" || channel === "info" || channel === "other") &&
    (!roll || !roll.secret)
  ) {
    dispatch(activeRoomEffectByText(roomId, effectText));
  }

  return addDoc(messagesRef(roomId), data).then((res) => {
    sendEvent("addRoomMessage", { value: ~~(Date.now() - startTime) });
    return res;
  });
};

const bgmHandler: SlashCommandHandler = async ({
  inputText,
  dispatch,
  getState,
}) => {
  const [, url, ...args] = inputText.split(/\s+/);

  if (!isValidUrl(url)) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/bgm コマンドに有効でないURLが指定されました。"
          ))
      )
    );
    return true;
  }

  const state = getState();
  const enableGoCdn = state.app.state.config.enabledGoCdnOnAudio;

  const isPlayable = await AudioPlayer.isPlayableAudioUrl(
    toCDNUrl(url, { enableGoCdn })
  );
  if (!isPlayable) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/bgm コマンドで指定されたURLが再生できないファイルでした。"
          ))
      )
    );
    return true;
  }

  const options = parseBgmOptions(args);

  if (
    !Number.isFinite(options.volume) ||
    options.volume > 1 ||
    options.volume < 0
  ) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/bgm コマンドで指定するボリュームの値は0以上・1以下としてください。"
          ))
      )
    );
    return true;
  }

  switch (options.slot) {
    case "bgm01":
      dispatch(
        updateCurrentRoomField({
          mediaUrl: url,
          mediaType: "file",
          mediaName: options.name,
          mediaVolume: options.volume,
          mediaRepeat: options.loop,
        })
      );
      break;
    case "bgm02":
      dispatch(
        updateCurrentRoomField({
          soundUrl: url,
          soundName: options.name,
          soundVolume: options.volume,
          soundRepeat: options.loop,
        })
      );
      break;
    default:
      dispatch(
        appStateMutate(
          (state) =>
            (state.errorSnackbarMessage = i18next.t(
              "/bgm コマンドで不明なスロットが指定されました。"
            ))
        )
      );
  }

  return true;
};

const bgmStopHandler: SlashCommandHandler = async ({ dispatch }) => {
  dispatch(
    updateCurrentRoomField({
      mediaName: "",
      mediaUrl: null,
      mediaRepeat: true,
      mediaType: "",
      mediaVolume: 0,
      soundName: "",
      soundUrl: null,
      soundRepeat: true,
      soundVolume: 0,
    })
  );

  return true;
};

type BgmOptions = {
  slot: string;
  name: string;
  volume: number;
  loop: boolean;
};

const parseBgmOptions = (args: string[]): BgmOptions => {
  const options: BgmOptions = {
    slot: "bgm01",
    name: "",
    volume: 1,
    loop: true,
  };

  for (const arg of args) {
    const a = arg.toLowerCase();
    if (a.startsWith("volume:")) {
      options.volume = Number.parseFloat(a.substring("volume:".length));
    } else if (a.startsWith("slot:")) {
      options.slot = a.substring("slot:".length);
    } else if (a.startsWith("name:")) {
      options.name = a.substring("name:".length);
    } else if (a === "loop:true") {
      options.loop = true;
    } else if (a === "loop:false") {
      options.loop = false;
    }
  }

  return options;
};

const passphrase = "d0_n0t_ev1";

type ExecArgs = {
  command: string;
  roomPackageId: string;
};

const isExecArgs = (data: any): data is ExecArgs => {
  return (
    typeof data.command === "string" && typeof data.roomPackageId === "string"
  );
};

const execHandler: SlashCommandHandler = async ({
  inputText,
  dispatch,
  getState,
}) => {
  const base64 = inputText.substring("/exec ".length);
  let args;

  try {
    const decrypted = AES.decrypt(base64, passphrase);
    const json = decrypted.toString(Utf8);
    args = JSON.parse(json);
  } catch {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage =
            i18next.t("コマンドの実行に失敗しました。"))
      )
    );
    return true;
  }

  const state = getState();
  const room = getCurrentRoom(state);
  const roomPackageId =
    room?.parentRoomPackageId || room?.publishedRoomPackageId;

  // ループ防止
  if (
    !isExecArgs(args) ||
    args.roomPackageId !== roomPackageId ||
    args.command.startsWith("/exec ")
  ) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage =
            i18next.t("コマンドの実行に失敗しました。"))
      )
    );
    return true;
  }

  dispatch(addMessageCurrentRoom(args.command, false));
  return true;
};

const sceneHandler: SlashCommandHandler = async ({
  roomId,
  inputText,
  dispatch,
  getState,
  isPro,
}) => {
  const sceneName = inputText.substring("/scene ".length);

  if (!sceneName) {
    return false;
  }

  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/scene コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const state = getState();
  const sceneId = getRoomSceneIdByName(state, sceneName);
  if (!sceneId) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "指定されたシーンがありませんでした。\n[シーン名] {{sceneName}}",
            { sceneName }
          ))
      )
    );
    return true;
  }

  dispatch(applyScene(roomId, sceneId));
  dispatch(addUndoSceneChange(sceneId));
  return true;
};

const sceneWithoutBgmHandler: SlashCommandHandler = async ({
  roomId,
  inputText,
  dispatch,
  getState,
  isPro,
}) => {
  const sceneName = inputText.substring("/scene-without-bgm ".length);

  if (!sceneName) {
    return false;
  }

  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/scene コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const state = getState();
  const sceneId = getRoomSceneIdByName(state, sceneName);
  if (!sceneId) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "指定されたシーンがありませんでした。\n[シーン名] {{sceneName}}",
            { sceneName }
          ))
      )
    );
    return true;
  }

  dispatch(applyScene(roomId, sceneId, { withoutBgm: true }));
  dispatch(addUndoSceneChange(sceneId, true));
  return true;
};

const varHandler: SlashCommandHandler = async ({
  inputText,
  roomId,
  dispatch,
  getState,
}) => {
  const [, label, value] = inputText.split(" ", 3);
  if (!label) {
    return false;
  }

  if (!getHasEditableRole(getState())) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "/var コマンドを実行できるのはルームマスターのみです。"
          ))
      )
    );
    return true;
  }

  const isOverflowLabel = label.length > 16;
  const isOverflowValue = value && value.length > 64;

  if (isOverflowLabel || isOverflowValue) {
    const errorMessage =
      (isOverflowLabel ? i18next.t("ラベルは16文字以下としてください。") : "") +
      (isOverflowValue ? i18next.t("値は64文字以下としてください。") : "");
    dispatch(
      appStateMutate((state) => (state.errorSnackbarMessage = errorMessage))
    );
    return true;
  }

  const state = getState();
  const room = getCurrentRoom(state);
  if (!room) {
    return true;
  }

  const variables = room.variables;
  const index = room.variables.findIndex((v) => v.label === label);
  if (index >= 0) {
    const prevValue = variables[index].value;
    dispatch(
      updateCurrentRoom({
        variables: produce(variables, (draft) => {
          if (value) {
            draft[index].value = value;
          } else {
            draft.splice(index, 1);
          }
        }),
      })
    );

    const systemMessage = value
      ? `/system [ room ] ${label} : ${prevValue} → ${value}`
      : `/system [ room ] ${label} : Deleted`;
    dispatch(
      addMessage(
        roomId,
        null,
        {
          text: systemMessage,
          name: "system",
          channel: "main",
        },
        false
      )
    );
  } else if (variables.length >= 16) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "上限に達しているため、新しくルーム変数を登録できませんでした。ルーム変数は最大16個です。"
          ))
      )
    );
    return true;
  } else if (value) {
    dispatch(
      updateCurrentRoom({ variables: [...variables, { label, value }] })
    );
    dispatch(
      addMessage(
        roomId,
        null,
        {
          text: `/system [ room ] ${label} : ${value}`,
          name: "system",
          channel: "main",
        },
        false
      )
    );
  } else {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "指定されたラベルの変数がありませんでした。\n[ラベル] {{label}}",
            { label }
          ))
      )
    );
  }
  return true;
};

const rollTableHandler: SlashCommandHandler = async ({
  roomId,
  inputText,
  characterId,
  textData,
  dispatch,
  getState,
  startTime,
  isPro,
}) => {
  const text = inputText.substring("/roll-table ".length);
  const state = getState();
  const room = getRoomById(state, roomId);
  const character = getCharacterById(state, characterId);
  const diceSkinSettings = character._id
    ? character.diceSkin
    : state.entities.userSetting.diceSkin;

  const roll = await rollTable(text);
  if (!roll) {
    dispatch(
      appStateMutate(
        (state) =>
          (state.errorSnackbarMessage = i18next.t(
            "オリジナル表の実行に失敗しました。書式を確認してください。"
          ))
      )
    );
    return true;
  }
  const rollText = text.split("\n");

  const message: UpdateMessage = {
    text: `${rollText[0]} ${rollText[1]}`,
    extend: {
      roll: {
        ...roll,
        skin: filterDiceSkinSettings(
          diceSkinSettings,
          room.diceBotSystem,
          isPro
        ),
      },
    },
  };

  textData.color = characterId
    ? character.color
    : getAppState(state, "roomChatColor");
  textData.iconUrl = character.iconUrl;

  sendMessage({
    message,
    type: "text",
    roomId,
    characterId,
    textData,
    getState,
    startTime,
    dispatch,
  });
  return true;
};

const nopHandler: SlashCommandHandler = async () => {
  return true;
};

const slashCommands: Record<string, SlashCommandHandler> = {
  bgm: bgmHandler,
  "bgm-stop": bgmStopHandler,
  exec: execHandler,
  omikuji: omikujiHandler,
  play: playHandler,
  pdf: pdfHandler,
  scene: sceneHandler,
  "scene-without-bgm": sceneWithoutBgmHandler,
  save: saveHandler,
  load: loadHandler,
  reset: resetHandler,
  var: varHandler,
  "roll-table": rollTableHandler,
};

export type MessageData = {
  name: string;
  iconImage?: string | null;
  iconUrl?: string | null;
  color: string;
  text: string;
  type: MessageType;
  extend: {
    roll?: {
      result: string;
      dices: { kind: string; value: number; faces: number }[];
      secret: boolean;
      success: boolean;
      failure: boolean;
      critical: boolean;
      fumble: boolean;
    };
  };
  edited: boolean;
  channel: string;
  channelName: string;
  createdAt: number;
  updatedAt: number;
};

type RoomLogHtmlSourceMessage = Omit<RoomLogHtmlMessage, "iconDataUrl"> & {
  iconUrl: string | null;
};

type RoomLogHtmlFile = Omit<FileEntry, "name">;

type RoomLogHtmlChannel = {
  channel: string;
  channelName: string;
  messages: RoomLogHtmlMessage[];
  files: RoomLogHtmlFile[];
};

const LogHTMLMessagesTemplate = `
<% forEach(messages, function(message) { %>
<p style="color:<%- message.color %>;">
  <span><% if(message.channelName) { %> [<%- message.channelName %>]<% } %></span>
  <span><%- message.name %></span> <% if (message.to) { %> >> <span><%- message.toName %></span><% } %>:
  <span>
    <%= escape(message.text).split('\\n').join('<br>') %> <% if (message.extend.roll) { %><%- message.extend.roll.result %><% } %>
  </span>
</p>
<% }); %>`;
const renderMessageElementsHTML = template(LogHTMLMessagesTemplate);

type DownloadRoomLogOptions = {
  uid: string;
  roomId: string;
  includeSecret: boolean;
  channel: string | null;
  channelName: string;
};

export const downloadRoomLog =
  ({
    uid,
    roomId,
    includeSecret,
    channel,
    channelName,
  }: DownloadRoomLogOptions): DefaultThunk =>
  async (dispatch, getState) => {
    const isPro = getIsPro(getState());
    dispatch(
      appStateMutate((state) => {
        state.progress = {
          isVisible: true,
          value: 0,
          total: 0,
        };
      })
    );

    try {
      const roomRef = doc(db, "rooms", roomId);
      const roomSnapshot = await getDoc(roomRef);
      const room = roomSnapshot.data();
      if (!room) return;

      const fileName = `${room.name}[${channelName}].html`;
      const roomMessagesRef = messagesRef(roomId);
      const outputMaxSize = isPro ? 100_000 : 10_000;
      const messageCountQuery = channel
        ? query(roomMessagesRef, where("channel", "==", channel))
        : roomMessagesRef;
      const snapshot = await getCountFromServer(messageCountQuery);
      const totalCount = Math.min(snapshot.data().count, outputMaxSize);

      if (totalCount === 0) {
        dispatch(
          appStateMutate(
            (state) =>
              (state.errorSnackbarMessage =
                i18next.t("出力できるログがありません"))
          )
        );
        return;
      }

      const createMessageQuery = (
        startAfterDocument: QueryDocumentSnapshot<DocumentData> | null,
        limitSize: number
      ) => {
        const baseQuery = channel
          ? query(
              roomMessagesRef,
              where("channel", "==", channel),
              orderBy("createdAt", "desc"),
              limit(limitSize)
            )
          : query(
              roomMessagesRef,
              orderBy("createdAt", "desc"),
              limit(limitSize)
            );

        return startAfterDocument
          ? query(baseQuery, startAfter(startAfterDocument))
          : baseQuery;
      };

      const renderMessageParagraphs = (snapshot: QuerySnapshot): string => {
        const messages = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            data.id = doc.id;
            return data;
          })
          .filter((message) => {
            if (includeSecret) {
              return !message.to || message.from === uid || message.to === uid;
            } else {
              return !message.to;
            }
          })
          .reverse()
          .map((message) => {
            if (
              room.owner !== uid &&
              message.from !== uid &&
              message.extend.roll &&
              message.extend.roll.secret
            ) {
              message.text = "シークレットダイス";
              message.extend.roll.result = "???";
              message.extend.roll.dices = [];
            }
            return message;
          });

        return renderMessageElementsHTML({ messages, escape, forEach });
      };

      const getMessageElements = async (
        startAfterDocument: QueryDocumentSnapshot<DocumentData> | null,
        rendered: string,
        messages: number,
        limitSize: number,
        max: number
      ): Promise<string> => {
        const messagesQuery = createMessageQuery(startAfterDocument, limitSize);
        const messagesSnapshot = await getDocs(messagesQuery);
        const { docs, size } = messagesSnapshot;
        const elements = renderMessageParagraphs(messagesSnapshot) + rendered;
        const sizeFetchedMessages = messages + size;

        dispatch(
          appStateMutate((state) => {
            state.progress.value = sizeFetchedMessages;
            state.progress.total = totalCount;
          })
        );

        if (size > 0 && max > sizeFetchedMessages) {
          return getMessageElements(
            docs[docs.length - 1],
            elements,
            messages + size,
            limitSize,
            max
          );
        } else {
          return elements;
        }
      };

      const elements = await getMessageElements(
        null,
        "",
        0,
        1000,
        outputMaxSize
      );
      const resultHtml = `<!DOCTYPE html>
<html lang="ja">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta http-equiv="X-UA-Compatible" content="ie=edge" />
    <title>ccfolia - logs</title>
  </head>
  <body>
    ${elements}
  </body>
</html>`;
      const blob = new Blob([resultHtml], {
        type: "text/html",
      });
      saveAs(blob, fileName);
    } catch (err) {
      console.error(err);
      window.alert(i18next.t("処理に失敗しました"));
    } finally {
      dispatch(
        appStateMutate((state) => {
          state.progress = {
            isVisible: false,
            value: 0,
            total: 0,
          };
        })
      );
    }
  };

export const downloadRoomLogJson =
  (roomId: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const isPro = getIsPro(state);
    const roomRef = doc(db, "rooms", roomId);
    const roomSnapshot = await getDoc(roomRef);
    const room = roomSnapshot.data();
    if (!room) return;
    dispatch(
      appStateMutate((state) => {
        state.progress = {
          isVisible: true,
          value: 0,
          total: 0,
        };
      })
    );

    try {
      const files = isPro
        ? await downloadRoomLogJsonForPro(dispatch, roomId, room.name)
        : await downloadRoomLogJsonForBasic(dispatch, roomId, room.name);

      if (files.length === 0) {
        dispatch(
          appStateMutate(
            (state) =>
              (state.errorSnackbarMessage =
                i18next.t("出力できるログがありません"))
          )
        );
        return;
      }
      if (files.length === 1) {
        const file = files[0];
        saveAs(file.data, file.name);
      } else {
        const fe: FileEntries = {};
        files.forEach((file, index) => {
          const id = `log_${index}`;
          fe[id] = file;
        });
        const zipBlob = await zip(fe);
        const zipFileName = `${room.name}_log.zip`;
        saveAs(zipBlob, zipFileName);
      }
    } catch (err) {
      console.error(err);
      window.alert(i18next.t("処理に失敗しました"));
    } finally {
      dispatch(
        appStateMutate((state) => {
          state.progress = {
            isVisible: false,
            value: 0,
            total: 0,
          };
        })
      );
    }
  };

const downloadRoomLogJsonForPro = async (
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>,
  roomId: string,
  roomName: string
): Promise<FileEntry[]> => {
  const outputMaxSize = 100_000;
  let startAfterDocument: QueryDocumentSnapshot<DocumentData> | null = null;
  const cachedDataUrl = new CachedDataUrl({ resizeImageSize: 720 });
  let fileCount = 0;
  const fileType = "text/json";
  const files: FileEntry[] = [];

  const snapshot = await getCountFromServer(messagesRef(roomId));
  const totalCount = snapshot.data().count;
  if (!totalCount || totalCount === 0) return [];
  dispatch(
    appStateMutate((state) => {
      state.progress.total = totalCount;
    })
  );

  while (true) {
    // 1ファイル分のデータを取得
    const messageDataResult = await getMessageData(
      roomId,
      outputMaxSize,
      dispatch,
      startAfterDocument,
      cachedDataUrl
    );

    fileCount++;
    const fileName =
      fileCount === 1 && !messageDataResult.nextCursor
        ? `${roomName}_log.json`
        : `${roomName}_log_${fileCount}.json`;
    const blob = new Blob([JSON.stringify(messageDataResult.result, null, 2)], {
      type: fileType,
    });
    files.push({
      name: fileName,
      data: blob,
      type: fileType,
    });

    if (!messageDataResult.nextCursor) {
      break;
    }
    startAfterDocument = messageDataResult.nextCursor;
  }
  return files;
};

const downloadRoomLogJsonForBasic = async (
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>,
  roomId: string,
  roomName: string
): Promise<FileEntry[]> => {
  const outputMaxSize = 10_000;
  const cachedDataUrl = new CachedDataUrl({ resizeImageSize: 360 });
  const fileType = "text/json";
  const snapshot = await getCountFromServer(messagesRef(roomId));
  const totalCount = snapshot.data().count;
  if (!totalCount || totalCount === 0) return [];

  dispatch(
    appStateMutate((state) => {
      state.progress.total = Math.min(outputMaxSize, totalCount);
    })
  );

  // 1万件分のデータを取得
  const messageDataResult = await getMessageData(
    roomId,
    outputMaxSize,
    dispatch,
    null,
    cachedDataUrl,
    true
  );

  const blob = new Blob([JSON.stringify(messageDataResult.result, null, 2)], {
    type: fileType,
  });
  return [
    {
      name: `${roomName}_log.json`,
      data: blob,
      type: fileType,
    },
  ];
};

const getMessageData = async (
  roomId: string,
  limitNum: number,
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  cachedDataUrl: CachedDataUrl,
  isReverse?: boolean
) => {
  // 1ファイル分(10万件)のメッセージを取得
  const messagesResult = await getMessages(
    roomId,
    limitNum,
    dispatch,
    cursor,
    isReverse
  );
  const messages = messagesResult.messages;

  // 画像URLの一覧を一意になるように抽出
  const uniqIconUrls: string[] = uniq(
    messages
      .map((message) => message.iconUrl)
      .filter((iconUrl): iconUrl is string => iconUrl != null)
  );

  // base64に変換された画像一覧を作成
  const dataUrls = await cachedDataUrl.getDataUrls(uniqIconUrls);
  const images: Record<string, string> = Array.from(dataUrls.values()).reduce(
    (images: Record<string, string>, image) => {
      if (image) {
        images[image.id] = image.data;
      }
      return images;
    },
    {}
  );

  // メッセージ情報を作成
  const resultMessages = messages.map((message) => {
    const { iconUrl, ...data } = message;
    data.iconImage = null;
    if (iconUrl) {
      const image = dataUrls.get(iconUrl);
      data.iconImage = image?.id || null;
    }
    return data;
  });

  return {
    nextCursor: messagesResult.nextCursor,
    result: {
      messages: resultMessages,
      images,
    },
  };
};

const getMessages = async (
  roomId: string,
  limitNum: number,
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  isReverse?: boolean
) => {
  const createMessageQuery = (
    startAfterDocument: QueryDocumentSnapshot<DocumentData> | null
  ) => {
    const baseQuery = query(
      messagesRef(roomId),
      orderBy("createdAt", isReverse ? "desc" : "asc"),
      limit(1000)
    );

    return startAfterDocument
      ? query(baseQuery, startAfter(startAfterDocument))
      : baseQuery;
  };

  let messagesCount = 0;
  let nextCursor: QueryDocumentSnapshot<DocumentData> | null = cursor ?? null;
  const messages: MessageData[] = [];
  while (true) {
    const messagesQuery = createMessageQuery(nextCursor);
    const messagesSnapshot = await getDocs(messagesQuery);
    const { docs, size } = messagesSnapshot;

    if (size === 0) {
      nextCursor = null;
      break;
    }

    messagesCount += size;
    docs.forEach((doc) => {
      const data = doc.data() as Message;
      const roll = data.extend.roll;
      messages.push({
        name: data.name,
        iconUrl: data.iconUrl,
        color: data.color,
        text: data.text,
        type: data.type,
        extend: {
          roll: roll
            ? {
                result: roll.result,
                dices: roll.dices,
                secret: roll.secret,
                success: roll.success,
                failure: roll.failure,
                critical: roll.critical,
                fumble: roll.fumble,
              }
            : undefined,
        },
        edited: data.edited,
        channel: data.channel,
        channelName: data.channelName,
        createdAt: data.createdAt.toMillis(),
        updatedAt: data.updatedAt.toMillis(),
      });
    });
    dispatch(
      appStateMutate((state) => {
        state.progress.value += size;
      })
    );

    nextCursor = docs[docs.length - 1];
    if (messagesCount >= limitNum) {
      break;
    }
  }

  return {
    messages: isReverse ? reverse(messages) : messages,
    nextCursor,
  };
};

export const downloadRoomLogHtml =
  (roomId: string): DefaultThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const isPro = getIsPro(state);
    const roomRef = doc(db, "rooms", roomId);
    const roomSnapshot = await getDoc(roomRef);
    const room = roomSnapshot.data();
    if (!room) return;
    dispatch(
      appStateMutate((state) => {
        state.progress = {
          isVisible: true,
          value: 0,
          total: 0,
        };
      })
    );

    try {
      const outputMaxSize = isPro ? 100_000 : 10_000;
      const snapshot = await getCountFromServer(messagesRef(roomId));
      const totalCount = snapshot.data().count;
      if (totalCount === 0) {
        dispatch(
          appStateMutate(
            (state) =>
              (state.errorSnackbarMessage =
                i18next.t("出力できるログがありません"))
          )
        );
        return;
      }
      dispatch(
        appStateMutate((state) => {
          state.progress.total = isPro
            ? totalCount
            : Math.min(totalCount, outputMaxSize);
        })
      );

      let startAfterDocument: QueryDocumentSnapshot<DocumentData> | null = null;
      const imageCache = new RoomLogHtmlImageCache();
      const allChannelName = i18next.t("すべて");
      const allFiles: RoomLogHtmlFile[] = [];
      const channels = new Map<string, RoomLogHtmlChannel>();
      const defaultChannelNames = {
        main: i18next.t("メイン"),
        info: i18next.t("情報"),
        other: i18next.t("雑談"),
      };

      // 「すべて」用のHTMLは、取得した上限件数ごとに作成する
      while (true) {
        const messagesResult = await getHtmlLogMessages(
          roomId,
          outputMaxSize,
          dispatch,
          startAfterDocument,
          !isPro
        );
        const messages = messagesResult.messages;
        if (messages.length === 0) break;

        // 同じ画像を繰り返し取得しないよう、この取得範囲で使われているURLだけを抽出する
        const iconUrls: string[] = uniq(
          messages
            .map((message) => message.iconUrl)
            .filter((iconUrl) => iconUrl != null)
        );
        const imageDataUrls = await imageCache.getDataUrls(iconUrls);

        // Firestoreから取得したデータを、HTMLの描画に必要な形式へ変換する
        const htmlMessages: RoomLogHtmlMessage[] = messages.map((message) => {
          const { iconUrl, ...data } = message;
          return {
            ...data,
            channelName:
              defaultChannelNames[data.channel] ||
              data.channelName ||
              data.channel,
            iconDataUrl: iconUrl ? imageDataUrls.get(iconUrl) || null : null,
          };
        });

        // 全チャンネルを含む「すべて」は、現在の取得範囲をそのまま1ファイルにする
        allFiles.push(
          createRoomLogHtmlFile(room.name, allChannelName, htmlMessages, true)
        );

        // 各メッセージをチャンネルごとのバッファへ振り分ける
        htmlMessages.forEach((message) => {
          const channel = message.channel || "main";
          const channelData = channels.get(channel);
          if (channelData) {
            channelData.messages.push(message);
            return;
          }

          channels.set(channel, {
            channel,
            channelName:
              defaultChannelNames[channel] || message.channelName || channel,
            messages: [message],
            files: [],
          });
        });

        // チャンネル単体で上限件数に達した場合だけ、バッファから切り出してHTMLにする
        channels.forEach((channel) => {
          while (channel.messages.length >= outputMaxSize) {
            channel.files.push(
              createRoomLogHtmlFile(
                room.name,
                channel.channelName,
                channel.messages.splice(0, outputMaxSize)
              )
            );
          }
        });

        if (!isPro || !messagesResult.nextCursor) break;
        startAfterDocument = messagesResult.nextCursor;
      }

      // 上限件数に満たずチャンネルのバッファに残ったログを、最後の1ファイルにする
      channels.forEach((channel) => {
        if (channel.messages.length > 0) {
          channel.files.push(
            createRoomLogHtmlFile(
              room.name,
              channel.channelName,
              channel.messages
            )
          );
        }
      });

      if (allFiles.length === 0) return;
      const channelOrder = { main: 0, info: 1, other: 2 };

      // 「すべて」、デフォルトチャンネル、その他のチャンネルの順に並べる
      const groupedFiles = [
        { channelName: allChannelName, files: allFiles },
        ...Array.from(channels.values()).sort(
          (a, b) =>
            (channelOrder[a.channel] ?? Number.MAX_SAFE_INTEGER) -
            (channelOrder[b.channel] ?? Number.MAX_SAFE_INTEGER)
        ),
      ];
      const files = createRoomLogHtmlFileEntries(room.name, groupedFiles);
      const fileEntries: FileEntries = {};

      // 生成したHTMLをすべて同じZIPへ格納する
      files.forEach((file, index) => {
        fileEntries[`html_log_${index}`] = file;
      });
      const zipBlob = await zip(fileEntries);
      saveAs(zipBlob, `${room.name}_html_log.zip`);
    } catch (err) {
      console.error(err);
      window.alert(i18next.t("処理に失敗しました"));
    } finally {
      dispatch(
        appStateMutate((state) => {
          state.progress = {
            isVisible: false,
            value: 0,
            total: 0,
          };
        })
      );
    }
  };

const getHtmlLogMessages = async (
  roomId: string,
  limitNum: number,
  dispatch: ThunkDispatch<DefaultRootState, unknown, any>,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  isReverse?: boolean
) => {
  const createMessageQuery = (
    startAfterDocument: QueryDocumentSnapshot<DocumentData> | null
  ) => {
    const baseQuery = query(
      messagesRef(roomId),
      orderBy("createdAt", isReverse ? "desc" : "asc"),
      limit(1000)
    );

    return startAfterDocument
      ? query(baseQuery, startAfter(startAfterDocument))
      : baseQuery;
  };

  let messagesCount = 0;
  let nextCursor: QueryDocumentSnapshot<DocumentData> | null = cursor ?? null;
  const messages: RoomLogHtmlSourceMessage[] = [];

  // 1回の取得件数を抑えるため、Firestoreから1,000件ずつ取得して上限まで追加する
  while (true) {
    const messagesQuery = createMessageQuery(nextCursor);
    const messagesSnapshot = await getDocs(messagesQuery);
    const { docs, size } = messagesSnapshot;

    if (size === 0) {
      nextCursor = null;
      break;
    }

    messagesCount += size;

    // FirestoreのMessageから、HTMLで表示する項目だけを取り出す
    docs.forEach((doc) => {
      const data = doc.data() as Message;
      const roll = data.extend?.roll;
      messages.push({
        name: data.name,
        iconUrl: data.iconUrl,
        color: data.color,
        text: data.text,
        type: data.type,
        roll: roll
          ? {
              result: roll.result,
              success: roll.success,
              failure: roll.failure,
              critical: roll.critical,
              fumble: roll.fumble,
            }
          : null,
        edited: data.edited,
        channel: data.channel,
        channelName: data.channelName,
        createdAt: data.createdAt.toMillis(),
      });
    });
    dispatch(
      appStateMutate((state) => {
        state.progress.value += size;
      })
    );

    nextCursor = docs[docs.length - 1];
    if (messagesCount >= limitNum) break;
  }

  return {
    messages: isReverse ? reverse(messages) : messages,
    nextCursor,
  };
};

const createRoomLogHtmlFile = (
  roomName: string,
  channelName: string,
  messages: RoomLogHtmlMessage[],
  showChannelName = false
): RoomLogHtmlFile => {
  const language = i18next.language || "ja";
  const html = renderRoomLogHTML({
    roomName,
    channelName,
    showChannelName,
    messages,
    language,
    labels: {
      edited: i18next.t("編集済"),
    },
  });

  return {
    data: new Blob([html], { type: "text/html;charset=utf-8" }),
    type: "text/html",
  };
};

const createRoomLogHtmlFileEntries = (
  roomName: string,
  groupedFiles: { channelName: string; files: RoomLogHtmlFile[] }[]
) => {
  const usedFileNames = new Map<string, number>();
  const safeRoomName = sanitizeLogFileName(roomName);

  // チャンネル内のファイル数が1件の場合は連番を付けず、複数の場合だけ連番を付ける
  return groupedFiles.flatMap(({ channelName, files }) =>
    files.map((file, index) => {
      const safeChannelName = sanitizeLogFileName(channelName);
      const suffix = files.length === 1 ? "" : `_${index + 1}`;
      const baseFileName = `${safeRoomName}[${safeChannelName}]${suffix}`;
      const duplicateCount = usedFileNames.get(baseFileName) || 0;
      usedFileNames.set(baseFileName, duplicateCount + 1);

      return {
        ...file,
        name: `${baseFileName}${
          duplicateCount === 0 ? "" : `_${duplicateCount + 1}`
        }.html`,
      };
    })
  );
};

const sanitizeLogFileName = (fileName: string) => {
  const invalidCharacters = '<>:"/\\|?*';
  const sanitized = Array.from(fileName, (character) =>
    character.charCodeAt(0) < 32 || invalidCharacters.includes(character)
      ? "_"
      : character
  )
    .join("")
    .replace(/[. ]+$/g, "");
  return sanitized || "log";
};
